/* eslint-disable camelcase */
import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useGlobals } from 'src/shared/contexts/appContext';
import { updateInvoiceAddressOnMember } from 'src/shared/services/memberService';
import { useSchemaEditInvoiceAddressWithReference } from '../schemas/useSchemaEditInvoiceAddressWithReference';
import { InvoiceAddressCreateForm } from './invoiceAddressCreateForm';
import { Member } from '../../models/member';

type InvoiceAddressHandleModalEditProps = {
  modalOpen: boolean;
  onClose: () => void;
  onStartOver: () => void;
  member: Member;
};

export const InvoiceAddressHandleModalEdit = (props: InvoiceAddressHandleModalEditProps) => {
  const { modalOpen, onClose, onStartOver, member } = props;

  const isMounted = useIsMounted();

  const { setDateStamp } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);

  const editInvoiceAddressRequestSchema = useSchemaEditInvoiceAddressWithReference();
  type EditInvoiceAddressFormInterface = InferType<typeof editInvoiceAddressRequestSchema>;

  const formMethods = useForm<EditInvoiceAddressFormInterface>({
    resolver: yupResolver(editInvoiceAddressRequestSchema),
    defaultValues: {
      invoiceAddress_id: member.invoiceAddress?.invoiceAddress_id ?? 0,
      name: member.invoiceAddress?.name ?? '',
      address: member.invoiceAddress?.address ?? '',
      postalcode: member.invoiceAddress?.postalcode ?? '',
      city: member.invoiceAddress?.city ?? '',
      country: member.invoiceAddress?.country ?? '',
      address2: member.invoiceAddress?.address2 ?? '',
      email: member.invoiceAddress?.email ?? '',
      name2: member.invoiceAddress?.name2 ?? '',
      invoiceReference: member.invoiceReference ?? '',
    },
  });

  const { handleSubmit } = formMethods;

  const onSubmit = useCallback(
    async (data: EditInvoiceAddressFormInterface) => {
      if (member.member_id) {
        try {
          await updateInvoiceAddressOnMember(member.member_id, data);
          if (isMounted()) {
            toast.success(t('member:invoiceAddress.updateSuccess'));
            setDateStamp(new Date());
            onClose();
          }
        } catch (e) {
          if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
        } finally {
          if (isMounted()) setLoading(false);
        }
      }
    },
    [member.member_id, isMounted, onClose, setDateStamp],
  );

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <>
      <GenericDialog
        open={modalOpen}
        onClose={() => onClose()}
        onSave={() => ConsoleHelper.log('save')}
        dialogTitle={t('member:invoiceAddress.editModalHeader')}
        padding={0}
        maxWidth='sm'
        dialogContent={
          <>
            <Alert severity='info'>
              <AlertTitle>{t('invoice:createInvoice.obs')}</AlertTitle>
              {t('invoice:createInvoice.warningAlreadyInUse')}
            </Alert>
            <Box
              sx={{
                position: 'relative',
                minHeight: '15rem',
                p: 4,
                pt: 2,
              }}
            >
              <LoadingOverlay loading={loading} />
              <Box>
                <GridWrapper>
                  <Grid item xs={12}>
                    <FormProvider {...formMethods}>
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Typography>{t('member:invoiceAddress.enterReference')}</Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <ControlledTextField name='invoiceReference' required label={t('member:invoiceAddress.reference')} />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} mt={2}>
                          <Grid item xs={12}>
                            <Typography>{t('member:invoiceAddress.pageHeader')}</Typography>
                          </Grid>
                          <InvoiceAddressCreateForm />
                        </Grid>
                      </form>
                    </FormProvider>
                  </Grid>
                </GridWrapper>
              </Box>
            </Box>
          </>
        }
        dialogActions={
          <Box display='flex' flexDirection='row' justifyContent='flex-end' width='100%'>
            <Box>
              <Button disableElevation variant='outlined' color='primary' onClick={onStartOver} disabled={loading} sx={{ mr: 1 }}>
                {t('common:button.startOver')}
              </Button>
              <Button disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
                {t('common:button.update')}
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};
